import { loadable, optionalMaybeNull } from '../mobx-utils';
import { types } from 'mobx-state-tree';

import { fetchApiEndpoint } from './flows/get-api-endpoint';
import { urls } from '../urls';

export const GiataGeoData = types.model('GiataGeoData', {
  latitude: types.number,
  longitude: types.number,
  accuracy: types.number,
});

export const GiataImageModel = types.model('GiataImageModel', {
  url: types.maybeNull(types.string),
  width: types.maybeNull(types.number),
  height: types.maybeNull(types.number),
  type: types.maybeNull(types.string),
});

export const TravellerModel = types.model('TravellerModel', {
  firstname: types.maybeNull(types.string),
  lastname: types.maybeNull(types.string),
  salutation: types.maybeNull(types.string),
});

export const HotelContentModel = types.model('HotelContentModel', {
  code: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
  street: types.maybeNull(types.string),
  zip: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  countryCode: types.maybeNull(types.string),
  organizerCode: types.maybeNull(types.string),
  organizerName: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  images: types.array(GiataImageModel),
  stars: types.maybeNull(types.number),
  geoData: types.maybeNull(GiataGeoData),
  roomCategory: types.maybeNull(types.string),
  catering: types.maybeNull(types.string),
  paxAssignment: types.maybeNull(types.array(TravellerModel)),
  roomCategoryCode: types.maybeNull(types.string),
  amountOfRooms: types.maybeNull(types.string),
  amountOfAdults: types.maybeNull(types.string),
  amountOfChildren: types.maybeNull(types.string),
  startDate: types.maybeNull(types.string),
  endDate: types.maybeNull(types.string),
});

export const ClientModel = types.model('ClientModel', {
  email: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  mobileNumber: types.maybeNull(types.string),
  nationality: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
});

export const FlightAdditionalInfo = types.model('FlightAdditionalInfo', {
  name: types.maybeNull(types.string),
  checkInNoteText: types.maybeNull(types.string),
  checkInLinkFromHour: types.maybeNull(types.string),
  checkInLinkText: types.maybeNull(types.string),
  checkInLinkUrl: types.maybeNull(types.string),
  addOnsText: types.maybeNull(types.string),
  addOnsUrl: types.maybeNull(types.string),
  handLuggageText: types.maybeNull(types.string),
  handLuggageUrl: types.maybeNull(types.string),
  deeplink: types.maybeNull(types.string),
});

export const FlightModel = types.model('FlightModel', {
  number: types.maybeNull(types.string),
  airline: types.maybeNull(types.string),
  airlineName: types.maybeNull(types.string),
  departureAirport: types.maybeNull(types.string),
  departureAirportName: types.maybeNull(types.string),
  departureAirportCountry: types.maybeNull(types.string),
  departureDate: types.maybeNull(types.string),
  departureTime: types.maybeNull(types.string),
  arrivalAirport: types.maybeNull(types.string),
  arrivalAirportName: types.maybeNull(types.string),
  arrivalAirportCountry: types.maybeNull(types.string),
  arrivalDate: types.maybeNull(types.string),
  arrivalTime: types.maybeNull(types.string),
  baggageWeight: types.maybeNull(types.string),
  additionalInformation: types.maybeNull(FlightAdditionalInfo),
});

export const PaymentModel = types.model('PaymentModel', {
  currency: types.maybeNull(types.string),
  downpayment: types.maybeNull(types.number),
  downpaymentDate8: types.maybeNull(types.string),
  finalPaymentDate8: types.maybeNull(types.string),
  gross: types.maybeNull(types.number),
  isInkasso: types.maybeNull(types.boolean),
  link: types.maybeNull(types.string),
  open: types.maybeNull(types.number),
  paymentType: types.maybeNull(types.string),
  paymentTypeText: types.maybeNull(types.string),
  utr: types.maybeNull(types.string),
});

export const AgencyModel = types.model('AgencyModel', {
  name: types.maybe(types.string),
  id: types.maybe(types.number),
  city: types.maybe(types.string),
  country: types.maybe(types.string),
  description: types.maybe(types.string),
  phone: types.maybe(types.string),
  postCode: types.maybe(types.string),
  streetAndNumber: types.maybe(types.string),
  email: types.maybe(types.string),
  nationality: types.maybe(types.string),
});

export const ActivityModel = types.model('ActivityModel', {
  name: types.string,
  description: types.string,
  highlight: types.string,
  images: types.array(
    types.model('ActivityImage', {
      link: types.string,
      title: types.maybe(types.string),
      alt: types.maybe(types.string),
      copyright: types.maybe(types.string),
    }),
  ),
  price: types.maybe(types.number),
  currency: types.maybe(types.string),
  bookingLink: types.maybe(types.string),
  bookingReference: types.maybe(types.string),
  seasons: optionalMaybeNull(types.array(types.string)),
});

export const BookingModel = types.model('BookingModel', {
  id: types.maybeNull(types.string),
  travellers: types.optional(types.array(TravellerModel), []),
  name: types.maybeNull(types.string),
  serviceName: types.maybeNull(types.string),
  hotelName: types.maybeNull(types.string),
  startDate: types.maybeNull(types.string),
  endDate: types.maybeNull(types.string),
  hotels: types.optional(types.array(HotelContentModel), []),
  flights: types.optional(types.array(FlightModel), []),
  returnFlights: types.optional(types.array(FlightModel), []),
  payment: PaymentModel,
  hasTransfer: types.maybeNull(types.boolean),
  transferType: types.maybeNull(types.string),
  myBentourCode: types.maybeNull(types.string),
  season: types.maybeNull(types.string),
  transferReference: types.maybeNull(types.string),
  agency: AgencyModel,
  bookedUpgrades: types.optional(
    types.array(types.maybeNull(types.string)),
    [],
  ),
  activities: types.optional(types.array(ActivityModel), []),
  activitySupplier: types.maybeNull(types.string),
  client: types.maybeNull(ClientModel),
  isMissingClientInfo: types.maybeNull(types.boolean),
});

export const BookingStore = types
  .model('BookingStore', {
    booking: loadable(BookingModel, fetchApiEndpoint(urls.api.bookings)),
  })
  .views((self) => ({
    // Returns an array of non-null geoLocation objects and their corresponding City name
    // e.g.   [{lat: 22.3 , lon:3.55 , name: 'Berlin'} , ... ]
    get validGeoDataFromHotels() {
      const hotelGeoData = self?.booking?.data?.hotels?.map((hotel) => [
        hotel?.geoData,
        hotel?.city,
        hotel?.country,
      ]);

      const validGeoData = hotelGeoData?.filter((hotel) => hotel[0] != null);

      let geolocations = [];
      if (validGeoData) {
        for (const geoLocation of validGeoData) {
          if (geoLocation?.[0]) {
            geolocations.push({
              lat: geoLocation[0]?.latitude,
              lon: geoLocation[0]?.longitude,
              name: geoLocation[1],
              country: geoLocation[2],
            });
          }
        }
      }

      return geolocations;
    },
  }));
